'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { usePathname, useRouter } from 'next/navigation'
import { LogOut, User, Settings, LayoutDashboard, Briefcase, FolderOpen, Users, FileText, CreditCard, Heart } from 'lucide-react'
import { UserCircleIcon } from 'lucide-react'
import { useProfileMode } from '@/hooks/useProfileMode'
import { getUserMenuItems } from './utils/headerMenuConfig'
import { handleLogout } from './handlers/headerHandlers'
import type { SupabaseClient } from '@supabase/supabase-js'

interface UserMenuProps {
  showUserMenu: boolean
  setShowUserMenu: (show: boolean) => void
  supabase: SupabaseClient | null
}

export default function UserMenu({ showUserMenu, setShowUserMenu, supabase }: UserMenuProps) {
  const router = useRouter()
  const pathname = usePathname()
  const menuRef = useRef<HTMLDivElement>(null)

  const { isCompany, isFreelancer } = useProfileMode()
  const menuItems = useMemo(() => getUserMenuItems(isCompany, isFreelancer), [isCompany, isFreelancer])

  const myHref = useMemo(() => {
    if (isCompany) return '/my/client'
    if (isFreelancer) return '/my/freelancer'

    const p = pathname || ''
    if (p.startsWith('/client') || p.startsWith('/my/client')) return '/my/client'
    if (p.startsWith('/freelancer') || p.startsWith('/my/freelancer')) return '/my/freelancer'
    return '/my/freelancer'
  }, [pathname, isCompany, isFreelancer])

  // ✅ (선택) md 미만에서 이 컴포넌트를 렌더하지 않는 게 가장 좋지만,
  // 혹시라도 렌더되는 경우를 대비해 runtime에서도 방어.
  const [isDesktop, setIsDesktop] = useState(true)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)') // md
    const apply = () => setIsDesktop(mq.matches)
    apply()
    mq.addEventListener?.('change', apply)
    return () => mq.removeEventListener?.('change', apply)
  }, [])

  useEffect(() => {
    if (!showUserMenu) return

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu, setShowUserMenu])

  const onLogout = async () => {
    setShowUserMenu(false)
    await handleLogout(supabase, router)
  }

  // ✅ Desktop: 드롭다운 토글
  // ✅ Mobile: 드롭다운 금지(여기서는 안전하게 마이페이지로 이동시키되, 원칙적으로는 모바일에서 렌더 자체를 막는게 더 좋음)
  const onClickProfileIcon = () => {
    if (!isDesktop) {
      router.push(myHref)
      return
    }
    setShowUserMenu(!showUserMenu)
  }

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={onClickProfileIcon}
        className="inline-flex items-center justify-center"
        aria-label="내 계정 메뉴"
        aria-expanded={showUserMenu}
        aria-haspopup="menu"
      >
        <UserCircleIcon size={28} color="#4a4a4a" strokeWidth={1.5} className="cursor-pointer" />
      </button>

      {showUserMenu && (
        <div
          role="menu"
          className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50"
        >
          {/* ✅ 상단에 ‘마이페이지’ 빠른 진입을 명시적으로 제공 (기존 클릭 이동 요구를 여기로 이동) */}
          <button
            type="button"
            onClick={() => {
              router.push(myHref)
              setShowUserMenu(false)
            }}
            className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors rounded-lg mx-1"
            role="menuitem"
          >
            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
            <span className="font-medium">마이페이지</span>
          </button>

          {menuItems.map((item) => {
            let icon = <LayoutDashboard className="w-4 h-4" />
            if (item.key === 'profile') icon = <User className="w-4 h-4" />
            else if (item.key === 'projects') icon = <Briefcase className="w-4 h-4" />
            else if (item.key === 'team' || item.key === 'teams') icon = <Users className="w-4 h-4" />
            else if (item.key === 'estimates') icon = <FileText className="w-4 h-4" />
            else if (item.key === 'billing') icon = <CreditCard className="w-4 h-4" />
            else if (item.key === 'bookmarks') icon = <Heart className="w-4 h-4" />
            else if (item.key === 'settings') icon = <Settings className="w-4 h-4" />
            else if (item.key === 'mypage') icon = <Settings className="w-4 h-4" />

            return (
              <button
                type="button"
                key={item.key}
                onClick={() => {
                  if (!item.href) return
                  router.push(item.href)
                  setShowUserMenu(false)
                }}
                className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-3 transition-colors rounded-lg mx-1"
                role="menuitem"
              >
                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">{icon}</div>
                <span className="font-medium">{item.label}</span>
              </button>
            )
          })}

          <div className="border-t border-gray-100 my-2" />

          <button
            type="button"
            onClick={onLogout}
            className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3 transition-colors rounded-lg mx-1"
            role="menuitem"
          >
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <LogOut className="w-4 h-4 text-red-600" />
            </div>
            <span className="font-medium">로그아웃</span>
          </button>
        </div>
      )}
    </div>
  )
}
